/**
 * Turn a stored failure reason into something a customer can read.
 *
 * `failureReason` is written for us, not for them. It is printed straight onto
 * the order page today, so customers have been shown things like:
 *
 *   Add ticket failed: Unexpected token 'o', "no healthy upstream" is not valid JSON
 *   Add ticket failed: Cart with id = 0e3ac2a5-6104-41b0-9761-f2252043f27c not found!
 *   Add ticket failed: Only valid for children under %maxAge% years old
 *   CC: Unexpected token '<', "<?xml vers"... is not valid JSON
 *   No FlixBus booking was created. Recorded retrospectively -- this order predates the fix...
 *
 * A JavaScript parse error, a cart UUID, an unfilled placeholder, and an
 * internal note about a historic data fix. 67 order items carry one of these
 * and every one is on screen.
 *
 * So: recognise the cases worth explaining, and say nothing technical about
 * the rest. The exact text is still kept on the order for Admin, where
 * somebody can act on it.
 *
 * The rule is deliberately one-way. Anything unmatched falls through to the
 * generic sentence -- a new supplier error can never reach a customer just
 * because nobody added a pattern for it.
 */

const GENERIC =
  "We were not able to complete this item. You can request a refund below, or contact our support team.";

type Rule = { match: RegExp; message: string };

/*
 * Ordered: the first match wins, so put the specific before the general.
 * Every message here is something the customer can either act on or at least
 * understand. Where they can do nothing, it says so plainly rather than
 * implying they got something wrong.
 */
const RULES: Rule[] = [
  {
    // "%maxAge%" is FlixBus's own placeholder, left unfilled by their API.
    match: /only valid for children under/i,
    message:
      "A child ticket only covers passengers up to 14 years old. This passenger needs to be booked as an adult.",
  },
  {
    match: /needs a date of birth|date should be in the format/i,
    message:
      "We need the child passenger's date of birth to complete this booking.",
  },
  {
    match: /gender does not match/i,
    message:
      "The seat chosen is reserved for a different passenger gender. Please choose another seat.",
  },
  {
    match: /must be listed with the country code/i,
    message:
      "The phone number needs to include its country code, for example +49.",
  },
  {
    match: /cart .*not found|cart_not_found|reservation has long expired|seat reservation has/i,
    message:
      "The booking was not confirmed in time and the reservation expired before it could be completed.",
  },
  {
    match: /missing passenger information/i,
    message:
      "Some passenger details did not reach the bus operator, so the booking could not be completed.",
  },
  {
    match: /no flixbus booking was created|no booking was completed/i,
    message: "This booking was not completed with the bus operator.",
  },
  {
    // Supplier outages: "no healthy upstream", HTTP 500, XML parsed as JSON.
    match: /no healthy upstream|http 5\d\d|not valid json|unexpected token/i,
    message:
      "Our supplier was temporarily unavailable, so this item could not be delivered.",
  },
  {
    match: /insufficient pins|out of stock/i,
    message: "This item was out of stock at the moment of purchase.",
  },
];

/**
 * What to show the customer for a failed item.
 *
 * Returns the generic sentence when nothing matches, when the field is empty,
 * and when the stored text looks like an internal note. Never returns the
 * stored text itself.
 */
export function deliveryFailureMessage(failureReason?: string | null): string {
  const raw = String(failureReason || "").trim();
  if (!raw) return GENERIC;

  for (const rule of RULES) {
    if (rule.match.test(raw)) return rule.message;
  }
  return GENERIC;
}

/**
 * What to pre-fill as the customer's own refund reason.
 *
 * The raw failure text used to be dropped into this box, so a JavaScript parse
 * error was submitted as the customer's stated reason for wanting their money
 * back, and that is what an admin then read on the Refunds screen.
 */
export function refundReasonForFailure(failureReason?: string | null): string {
  return deliveryFailureMessage(failureReason);
}
